import { Fragment, type ReactNode } from "react";

/**
 * WhatsApp's own formatting markers. Like WhatsApp, a marker only counts at a word's
 * edge, so "nombre_de_archivo" or "2*3*4" stay as they are.
 */
const INLINE =
  /(```[\s\S]+?```|`[^`\n]+`|(?<![\p{L}\p{N}*])\*(?!\s)[^*\n]+?(?<!\s)\*(?![\p{L}\p{N}*])|(?<![\p{L}\p{N}_])_(?!\s)[^_\n]+?(?<!\s)_(?![\p{L}\p{N}_])|(?<![\p{L}\p{N}~])~(?!\s)[^~\n]+?(?<!\s)~(?![\p{L}\p{N}~])|https?:\/\/[^\s<]+[^\s<.,;:!?)\]"'])/gu;

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    const token = m[0];
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const k = `${key}-${i++}`;
    if (token.startsWith("```")) out.push(<code key={k}>{token.slice(3, -3)}</code>);
    else if (token.startsWith("`")) out.push(<code key={k}>{token.slice(1, -1)}</code>);
    else if (token.startsWith("*")) out.push(<strong key={k}>{inline(token.slice(1, -1), k)}</strong>);
    else if (token.startsWith("_")) out.push(<em key={k}>{inline(token.slice(1, -1), k)}</em>);
    else if (token.startsWith("~")) out.push(<s key={k}>{inline(token.slice(1, -1), k)}</s>);
    else
      out.push(
        <a key={k} href={token} target="_blank" rel="noopener noreferrer">
          {token}
        </a>,
      );
    last = at + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const BULLET = /^\s*[-*•]\s+/;
const NUMBER = /^\s*(\d{1,3})[.)]\s+/;

/**
 * A message as WhatsApp shows it: *bold*, _italic_, ~strikethrough~, `code`, lists and
 * links, so the team sees what the customer sees. Built as React nodes, never HTML.
 */
export function WhatsAppText({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] as string;
    const list = BULLET.test(line) ? "ul" : NUMBER.test(line) ? "ol" : null;
    if (list) {
      const items: string[] = [];
      const start = Number(line.match(NUMBER)?.[1] ?? 1);
      while (i < lines.length && (list === "ul" ? BULLET : NUMBER).test(lines[i] as string)) {
        items.push((lines[i] as string).replace(list === "ul" ? BULLET : NUMBER, ""));
        i++;
      }
      const children = items.map((item, j) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the text is fixed once sent
        <li key={j}>{inline(item, `l${i}-${j}`)}</li>
      ));
      blocks.push(
        list === "ul" ? (
          <ul key={`b${i}`}>{children}</ul>
        ) : (
          <ol key={`b${i}`} start={start}>
            {children}
          </ol>
        ),
      );
      continue;
    }
    blocks.push(
      <Fragment key={`b${i}`}>
        {inline(line, `t${i}`)}
        {i < lines.length - 1 && !BULLET.test(lines[i + 1] ?? "") && !NUMBER.test(lines[i + 1] ?? "") ? "\n" : null}
      </Fragment>,
    );
    i++;
  }
  return <span className="wa-text">{blocks}</span>;
}
